import React from 'react';
import { Button, DialogContentText, Modal } from '@p81-common/ui';
import type { ModalProps } from '@p81-common/ui';
import CircularProgress from '@mui/material/CircularProgress';

/*
 * Confirmation gate for the stuck-job operator actions.
 *
 * Both of them are irreversible and act on a customer's production network:
 * force-release drops a mutex while the action behind it may still be running,
 * and retry re-issues a restart, rebuild or upgrade. They sit as two small icons
 * in a table row next to the "view" affordance, so a misclick is cheap to make
 * and expensive to have made. Follows the same local-ConfirmationModal pattern
 * as CS/EdgesList, which gates edge restart and deploy the same way.
 */

export type JobActionKind = 'release' | 'retry';

export interface PendingJobAction {
  kind: JobActionKind;
  networkId: string;
  customerId: string;
  networkName?: string;
  action?: string;
}

interface JobActionConfirmModalProps {
  open: ModalProps['open'];
  pending: PendingJobAction | null;
  isLoading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const buildCopy = (pending: PendingJobAction) => {
  const target = pending.networkName
    ? `${pending.networkName} (${pending.networkId})`
    : pending.networkId;
  const action = pending.action ? `"${pending.action}"` : 'action';

  if (pending.kind === 'release') {
    return {
      title: 'Force-release this job?',
      confirmLabel: 'Force-release',
      message:
        `This clears the lock held by the ${action} on ${target}. ` +
        'If that action is in fact still running on the platform, releasing now ' +
        'allows a second action to start alongside it. Prefer retry if you want ' +
        'the action re-issued rather than just unblocked.'
    };
  }

  return {
    title: 'Retry this action?',
    confirmLabel: 'Retry',
    message:
      `This releases the current lock and runs the ${action} on ${target} again. ` +
      'It is a real action against the customer’s network, not a status refresh.'
  };
};

const JobActionConfirmModal = ({
  open,
  pending,
  isLoading = false,
  onConfirm,
  onClose
}: JobActionConfirmModalProps) => {
  if (!pending) return null;

  const { title, message, confirmLabel } = buildCopy(pending);

  return (
    <Modal
      id="cloudManagementJobActionModal"
      open={open}
      dialogActionsProps={{
        children: (
          <>
            <Button
              variant="text"
              color="primary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              color={pending.kind === 'release' ? 'error' : 'primary'}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
              {confirmLabel}
            </Button>
          </>
        )
      }}
      dialogContentProps={{
        children: <DialogContentText>{message}</DialogContentText>
      }}
      dialogTitleProps={{ children: title }}
      onClose={onClose}
    />
  );
};

export default JobActionConfirmModal;
